import {
  LoaderCircle,
  RefreshCw,
  Image,
  FileText,
  BarChart2,
  Layers,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import PostsTab from "./PostsTab";
import type { Persona } from "@/api/personas";
import type { Post } from "@/api/posts";

interface PostTabsProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  sortBy: string;
  onSortChange: (sort: string) => void;
  isRefreshing: boolean;
  isPostsLoading: boolean;
  onRefresh: () => Promise<void>;
  posts: Post[];
  personaId: string;
  personaData: Persona;
}

const PostTabs: React.FC<PostTabsProps> = ({
  activeTab,
  onTabChange,
  sortBy,
  onSortChange,
  isRefreshing,
  isPostsLoading,
  onRefresh,
  posts,
  personaId,
  personaData,
}) => {
  console.debug("PostTabs -> personaId", personaId);
  // Count posts by type for badge displays
  const mediaPosts = posts.filter((post) => post.message_type === "media_post");
  const mediaEvaluationPosts = posts.filter(
    (post) => post.message_type === "media_evaluation",
  );
  const regularPosts = posts.filter((post) => post.message_type === "post");

  return (
    <Tabs
      defaultValue="all"
      value={activeTab}
      onValueChange={onTabChange}
      className="w-full"
    >
      <div className="flex flex-col space-y-3">
        <div className="flex justify-end items-center gap-2 mb-1">
          <Button
            variant="outline"
            className="h-10"
            onClick={onRefresh}
            disabled={isPostsLoading || isRefreshing}
          >
            {isRefreshing ? (
              <LoaderCircle className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            REFRESH
          </Button>

          <Select
            value={sortBy}
            onValueChange={onSortChange}
            disabled={isPostsLoading}
          >
            <SelectTrigger className="h-10 min-w-[140px]">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="newest">Newest First</SelectItem>
              <SelectItem value="oldest">Oldest First</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Tabs below */}
        <TabsList className="w-full grid grid-cols-4">
          <TabsTrigger
            value="all"
            className="gap-2 min-w-0 px-1 sm:px-3 relative"
          >
            <Layers className="h-4 w-4 min-w-4 min-h-4 flex-shrink-0" />
            <span className="hidden sm:inline">All Posts</span>
            {posts.length > 0 && activeTab === "all" && (
              <span className="absolute -top-1 -right-1 text-xs bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white px-1.5 py-0.5 rounded-full min-w-[1.2rem] text-center">
                {posts.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger
            value="media_post"
            className="gap-2 min-w-0 px-1 sm:px-3 relative"
          >
            <Image className="h-4 w-4 min-w-4 min-h-4 flex-shrink-0" />
            <span className="hidden sm:inline">Images</span>
            {mediaPosts.length > 0 && activeTab === "media_post" && (
              <span className="absolute -top-1 -right-1 text-xs bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-100 px-1.5 py-0.5 rounded-full min-w-[1.2rem] text-center">
                {mediaPosts.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger
            value="media_evaluation"
            className="gap-2 min-w-0 px-1 sm:px-3 relative"
          >
            <BarChart2 className="h-4 w-4 min-w-4 min-h-4 flex-shrink-0" />
            <span className="hidden sm:inline">Media</span>
            {mediaEvaluationPosts.length > 0 &&
              activeTab === "media_evaluation" && (
                <span className="absolute -top-1 -right-1 text-xs bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-100 px-1.5 py-0.5 rounded-full min-w-[1.2rem] text-center">
                  {mediaEvaluationPosts.length}
                </span>
              )}
          </TabsTrigger>
          <TabsTrigger
            value="post"
            className="gap-2 min-w-0 px-1 sm:px-3 relative"
          >
            <FileText className="h-4 w-4 min-w-4 min-h-4 flex-shrink-0" />
            <span className="hidden sm:inline">Posts</span>
            {regularPosts.length > 0 && activeTab === "post" && (
              <span className="absolute -top-1 -right-1 text-xs bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-100 px-1.5 py-0.5 rounded-full min-w-[1.2rem] text-center">
                {regularPosts.length}
              </span>
            )}
          </TabsTrigger>
        </TabsList>
      </div>

      <TabsContent value="all" className="mt-0">
        <PostsTab
          isLoading={isPostsLoading}
          posts={posts}
          personaData={personaData}
          onRefresh={onRefresh}
          isRefreshing={isRefreshing}
        />
      </TabsContent>

      <TabsContent value="media_post" className="mt-0">
        <PostsTab
          isLoading={isPostsLoading}
          posts={mediaPosts}
          personaData={personaData}
          onRefresh={onRefresh}
          isRefreshing={isRefreshing}
        />
      </TabsContent>

      <TabsContent value="media_evaluation" className="mt-0">
        <PostsTab
          isLoading={isPostsLoading}
          posts={mediaEvaluationPosts}
          personaData={personaData}
          onRefresh={onRefresh}
          isRefreshing={isRefreshing}
        />
      </TabsContent>

      <TabsContent value="post" className="mt-0">
        <PostsTab
          isLoading={isPostsLoading}
          posts={regularPosts}
          personaData={personaData}
          onRefresh={onRefresh}
          isRefreshing={isRefreshing}
        />
      </TabsContent>
    </Tabs>
  );
};

export default PostTabs;
